import {
    useEffect,
    useState,
    type FC,
    useRef
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type ITabBarIndicatorProps from "./type";
import type {
    TabBarIndicatorTargetType
} from "./type";
import stylesheet from "./stylesheet";
import {
    getTabBarIndicatorTarget
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useMotionTiming
} from "../../../../helpers/motion";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    getTabBarItemColors
} from "../item/stylesheet";

const TabBarIndicator: FC<ITabBarIndicatorProps> = ({
    labelBehaviour,
    customTheme,
    listWidth,
    cell
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const moveTiming = useMotionTiming("valueChange", customTheme);

    const fadeTiming = useMotionTiming("toggle", customTheme, "fade");

    const offset = useRef(new Animated.Value(0)).current;

    const opacity = useRef(new Animated.Value(0)).current;

    const isPlacedRef = useRef<boolean>(false);

    const [
        size,
        setSize
    ] = useState<Omit<TabBarIndicatorTargetType, "x">>({
        height: 0,
        width: 0,
        y: 0
    });

    const target = getTabBarIndicatorTarget({
        controlHeights,
        labelBehaviour,
        touchTargets,
        direction,
        listWidth,
        spaces,
        cell
    });

    useEffect(() => {
        if(!target) {
            isPlacedRef.current = false;

            Animated.timing(opacity, {
                useNativeDriver: Platform.OS !== "web",
                duration: fadeTiming.duration,
                easing: fadeTiming.easing,
                isInteraction: false,
                toValue: 0
            }).start();

            return;
        }

        setSize({
            height: target.height,
            width: target.width,
            y: target.y
        });

        if(!isPlacedRef.current) {
            isPlacedRef.current = true;

            offset.setValue(target.x);

            Animated.timing(opacity, {
                useNativeDriver: Platform.OS !== "web",
                duration: fadeTiming.duration,
                easing: fadeTiming.easing,
                isInteraction: false,
                toValue: 1
            }).start();

            return;
        }

        const animation = Animated.timing(offset, {
            useNativeDriver: Platform.OS !== "web",
            duration: moveTiming.duration,
            easing: moveTiming.easing,
            isInteraction: false,
            toValue: target.x
        });

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        target?.height,
        target?.width,
        target?.x,
        target?.y
    ]);

    return <Animated.View
        style={[
            stylesheet.container,
            {
                backgroundColor: getTabBarItemColors({
                    isDisabled: false,
                    isActive: true,
                    colors
                }).containerColor,
                ...getCornerStyle({
                    radius: radiuses.actions,
                    shapeConfigs
                }),
                transform: [
                    {
                        translateX: offset
                    }
                ],
                height: size.height,
                width: size.width,
                top: size.y,
                opacity
            }
        ]}
        aria-hidden={true}
    />;
};
export default TabBarIndicator;
